import React, { useState, useEffect } from 'react';
import { X, Search, Printer, Plus, Minus, Check, Loader2, Package, Receipt, Trash2 } from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useInventory } from './contexts/DataProviders';
import { useSettings } from './contexts/SettingsContext';
import apiService from './apiService';

const POSView = ({ setCurrentView }) => {
  const { inventoryItems } = useInventory();
  const { showAlert } = useAlert();
  const { appSettings } = useSettings();
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [saleDetails, setSaleDetails] = useState(null);

  // Default currency if not set in appSettings
  const currency = appSettings.currency || { code: 'KSH', symbol: 'Ksh.' };

  const filteredItems = inventoryItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddToCart = (item) => {
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    if (existingItem) {
      showAlert('Item already in cart. Update quantity in the cart.', 'info');
      return;
    }
    setCart([...cart, { ...item, quantity: 1, price: item.sellingPrice || 0 }]);
    setSearchTerm('');
  };

  const handleUpdateCartItem = (itemId, field, value) => {
    setCart(cart.map(cartItem =>
      cartItem.id === itemId ? { ...cartItem, [field]: value } : cartItem
    ));
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(cart.filter(cartItem => cartItem.id !== itemId));
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.quantity * item.price, 0).toFixed(2);
  };

  const formatCurrency = (amount) => {
    return `${currency.symbol}${parseFloat(amount).toFixed(2)}`;
  };

  const handleRecordSale = async () => {
    if (cart.length === 0 || cart.some(item => item.quantity <= 0 || item.price <= 0)) {
      showAlert('Please add valid items to the cart', 'error');
      return;
    }

    for (const item of cart) {
      const inventoryItem = inventoryItems.find(i => i.id === item.id);
      if (item.quantity > inventoryItem.quantity) {
        showAlert(`Only ${inventoryItem.quantity} ${inventoryItem.name} available in stock`, 'error');
        return;
      }
    }

    setIsProcessing(true);

    try {

      // Record the sale with createTransaction payload
      const sale = {
        staffId: 1, // Replace with actual staff ID (e.g., from logged-in user)
        items: cart.map(item => ({
          inventoryItemId: item.id, // Use inventoryItemId as per TransactionItemInput
          quantity: item.quantity
        })),
        paymentMethod: 'cash', // Hardcoded for now; consider adding a payment method selector
        patientId: null, // Nullable; add patient selection if needed
        organizationId: parseInt(localStorage.getItem('user_organization_id')) || null
      };

      const result = await apiService.recordSale(sale); // Updated to use createTransaction

      setSaleDetails({
        ...sale,
        items: cart.map(item => ({
          itemId: item.id,
          itemName: item.name,
          quantity: item.quantity,
          price: item.price,
          total: item.quantity * item.price
        })),
        total: parseFloat(calculateTotal()),
        date: new Date().toLocaleString()
      });
      setShowReceipt(true);
      showAlert('Sale recorded successfully!', 'success');
    } catch (error) {
      console.error('Failed to record sale:', error);
      showAlert('Failed to record sale. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
      setCart([]);
      setSearchTerm('');
    }
  };

  return (
    <div className="space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Point of Sale</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
        >
          <Plus className="w-4 h-4" />
          Record Sale
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">Record Sale</h3>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Search Item
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search inventory..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 pl-10 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                </div>

                {searchTerm && (
                  <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                    {filteredItems.length > 0 ? (
                      filteredItems.map(item => (
                        <div
                          key={item.id}
                          onClick={() => handleAddToCart(item)}
                          className={`p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 ${
                            cart.some(cartItem => cartItem.id === item.id) ? 'bg-blue-50 dark:bg-blue-900' : ''
                          }`}
                        >
                          <div className="font-medium">{item.name}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                            Stock: {item.quantity} {item.unit} | Price: {formatCurrency(item.sellingPrice || 0)}
                            </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-center text-gray-500 dark:text-gray-400">No items found</div>
                    )}
                  </div>
                )}
              </div>

              {cart.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Cart</h4>
                  {cart.map(item => (
                    <div key={item.id} className="border-b pb-2 mb-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{item.name}</span>
                        <button
                          onClick={() => handleRemoveFromCart(item.id)}
                          className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="block flex-1">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Quantity</span>
                          <div className="flex items-center mt-1">
                            <button
                              onClick={() => handleUpdateCartItem(item.id, 'quantity', Math.max(1, item.quantity - 1))}
                              className="p-2 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-l-lg"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleUpdateCartItem(item.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-full p-2 border-y border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-center"
                            />
                            <button
                              onClick={() => handleUpdateCartItem(item.id, 'quantity', item.quantity + 1)}
                              className="p-2 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-r-lg"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </label>
                        <label className="block flex-1">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Price per unit</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => handleUpdateCartItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg mt-1"
                          />
                        </label>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">Subtotal:</span>
                        <span className="text-gray-900 dark:text-gray-100">{formatCurrency(item.quantity * item.price)}</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center mt-4">
                    <span className="font-medium text-gray-900 dark:text-gray-100">Total:</span>
                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{formatCurrency(calculateTotal())}</span>
                  </div>
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={() => setCart([])}
                      className="bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                    >
                      Clear Cart
                    </button>
                    <button
                      onClick={handleRecordSale}
                      disabled={isProcessing}
                      className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-75"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Confirm Sale
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showReceipt && saleDetails && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Sale Receipt</h3>
              <button onClick={() => setShowReceipt(false)}>
                <X className="w-6 h-6 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" />
              </button>
            </div>

            <div className="border rounded-lg p-4 mb-4">
              <div className="text-center font-bold mb-2">LIPAY AFYA RECEIPT</div>
              <div className="text-center text-sm text-gray-500 mb-4">{saleDetails.date}</div>

              <div className="border-b pb-2 mb-2">
                {saleDetails.items.map(item => (
                  <div key={item.itemId} className="mb-2">
                    <div className="flex justify-between">
                      <span>Item:</span>
                      <span>{item.itemName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Quantity:</span>
                      <span>{item.quantity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Price per unit:</span>
                      <span>{formatCurrency(item.price)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(item.total)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between font-bold text-lg mt-4">
                <span>TOTAL:</span>
                <span>{formatCurrency(saleDetails.total)}</span>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => window.print()}
                className="bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print Receipt
              </button>
              <button
                onClick={() => {
                  setShowReceipt(false);
                  setShowModal(false);
                }}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setShowModal(true)}
            className="p-4 border border-dashed border-gray-300 rounded-lg text-center hover:bg-gray-50"
          >
            <div className="text-blue-500 mx-auto mb-2">
              <Plus className="w-8 h-8 mx-auto" />
            </div>
            <span>Record New Sale</span>
          </button>

          <button
            onClick={() => setCurrentView('inventory')}
            className="p-4 border border-dashed border-gray-300 rounded-lg text-center hover:bg-gray-50"
          >
            <div className="text-green-500 mx-auto mb-2">
              <Package className="w-8 h-8 mx-auto" />
            </div>
            <span>View Inventory</span>
          </button>

          <button
            onClick={() => showAlert('Sales history will be implemented in the next version', 'info')}
            className="p-4 border border-dashed border-gray-300 rounded-lg text-center hover:bg-gray-50"
          >
            <div className="text-purple-500 mx-auto mb-2">
              <Receipt className="w-8 h-8 mx-auto" />
            </div>
            <span>View Sales History</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default POSView;